import {
    Box,
    Chip,
    Link as MuiLink,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import {
    deriveGateState,
    isGateStale,
    type GateDoc,
    type GateMode,
    type GateUiState,
} from '../../types/gates';
import { compareGates, type SortContext } from './gateSort';
import { Fragment } from 'react';
import { RelativeDate } from '../misc/RelativeDate';
import { GateDecisionCell } from './GateDecisionCell';
import { TargetStrip } from './TargetStrip';
import { parseMarketQuestion, parseTargetContext } from './targetContext';

const STATE_LABEL: Record<GateUiState, string> = {
    awaiting_decision: 'AWAITING',
    increase_requested: 'INCREASE REQUESTED',
    approved: 'APPROVED',
    denied: 'HELD',
    closing: 'CLOSING',
};

function stateColor(state: GateUiState, palette: Theme['palette']): string {
    switch (state) {
        case 'awaiting_decision':
        case 'increase_requested':
            return palette.warning.main;
        case 'approved':
            return palette.success.main;
        case 'denied':
            // held, not an error — the position is fine, just frozen.
            return palette.warning.main;
        case 'closing':
            // sells to flat — shares the same alarmed tone as an error.
            return palette.error.main;
    }
}

function marketEnded(gate: GateDoc, now: number = Date.now()): boolean {
    return (
        gate.market_end_date !== null &&
        gate.market_end_date !== undefined &&
        new Date(gate.market_end_date).getTime() < now
    );
}

function fmtUpdated(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}.${mm}.${d.getFullYear()} ${hh}:${min}`;
}

export function GatesTable({
    gates,
    readOnly,
    highlightConditionId,
    mode,
    sortContext,
}: {
    gates: GateDoc[];
    readOnly: boolean;
    highlightConditionId: string | null;
    mode?: GateMode | null;
    /** Legacy-doc ordering fallback from useGates. */
    sortContext: SortContext;
}) {
    const theme = useTheme();

    if (gates.length === 0) {
        return (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body2">
                    No approval requests yet
                </Typography>
                <Typography variant="caption">
                    bogg writes a request here when a target needs new exposure
                    approved.
                </Typography>
            </Box>
        );
    }

    // Attention first, stale last, newest request first within a group.
    const sorted = [...gates].sort(
        compareGates(sortContext, mode ?? undefined)
    );

    return (
        <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Market</TableCell>
                        <TableCell>State</TableCell>
                        <TableCell align="right">Proposed</TableCell>
                        <TableCell align="right">Granted</TableCell>
                        <TableCell>Updated</TableCell>
                        <TableCell>Decision</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sorted.map(gate => {
                        const state = deriveGateState(gate);
                        const stale = isGateStale(gate);
                        // Strip row (#116) only when the target yields
                        // something to show — avoids an empty spacer row.
                        const hasTargetContext =
                            parseTargetContext(gate.target) !== null;
                        // Question title for the Market cell; falls back to slug/id.
                        const marketQuestion = parseMarketQuestion(gate.target);
                        // "Signal paused" is the heartbeat-only flavor of
                        // staleness (bogg stopped ticking but the market is
                        // still live) — distinct from an ended market, which
                        // the existing grey-out + INACTIVE label already
                        // covers (design/GATE.md §11).
                        const signalPaused = stale && !marketEnded(gate);
                        const color = stateColor(state, theme.palette);
                        const highlighted =
                            gate.condition_id === highlightConditionId;
                        const granted =
                            gate.decision?.action === 'approve'
                                ? gate.decision.approved_limit_usd === null
                                    ? 'unbounded'
                                    : `$${gate.decision.approved_limit_usd.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
                                : '—';
                        return (
                            <Fragment key={gate.condition_id}>
                            <TableRow
                                sx={{
                                    bgcolor: highlighted
                                        ? alpha(
                                              theme.palette.primary.main,
                                              0.08
                                          )
                                        : undefined,
                                    // Stale = inactive: market ended or bogg's heartbeat
                                    // stopped. Still decidable, just de-emphasized.
                                    opacity: stale ? 0.5 : 1,
                                    '& td': {
                                        borderColor: alpha(
                                            theme.palette.divider,
                                            0.4
                                        ),
                                        // The strip row below carries the
                                        // card's bottom border instead.
                                        borderBottom: hasTargetContext
                                            ? 'none'
                                            : undefined,
                                    },
                                }}
                            >
                                <TableCell sx={{ maxWidth: 320 }}>
                                    {gate.market_slug ? (
                                        <MuiLink
                                            href={`https://polymarket.com/market/${gate.market_slug}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            underline="hover"
                                            sx={{ fontSize: 13 }}
                                        >
                                            {marketQuestion ?? gate.market_slug}
                                        </MuiLink>
                                    ) : marketQuestion ? (
                                        <Typography sx={{ fontSize: 13 }}>
                                            {marketQuestion}
                                        </Typography>
                                    ) : (
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                fontFamily: 'monospace',
                                            }}
                                        >
                                            {gate.condition_id}
                                        </Typography>
                                    )}
                                    {(gate.first_requested_at ||
                                        signalPaused) && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.75,
                                                mt: 0.25,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            {gate.first_requested_at && (
                                                <Typography
                                                    sx={{
                                                        fontSize: 10,
                                                        color: 'text.secondary',
                                                    }}
                                                >
                                                    requested{' '}
                                                    <RelativeDate
                                                        date={
                                                            gate.first_requested_at
                                                        }
                                                    />
                                                </Typography>
                                            )}
                                            {signalPaused && (
                                                <Chip
                                                    label="signal paused"
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        height: 16,
                                                        fontSize: 9,
                                                        color: theme.palette
                                                            .text.disabled,
                                                        borderColor: alpha(
                                                            theme.palette.text
                                                                .disabled,
                                                            0.4
                                                        ),
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={
                                            stale
                                                ? `${STATE_LABEL[state]} · INACTIVE`
                                                : STATE_LABEL[state]
                                        }
                                        size="small"
                                        sx={{
                                            height: 20,
                                            fontSize: 10,
                                            fontWeight: 600,
                                            color: stale
                                                ? theme.palette.text.disabled
                                                : color,
                                            bgcolor: alpha(
                                                stale
                                                    ? theme.palette.text
                                                          .disabled
                                                    : color,
                                                0.12
                                            ),
                                        }}
                                    />
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontSize: 12,
                                    }}
                                >
                                    $
                                    {gate.proposed_limit_usd.toLocaleString(
                                        'en-US',
                                        { maximumFractionDigits: 0 }
                                    )}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontSize: 12,
                                    }}
                                >
                                    {granted}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontSize: 11,
                                        color: 'text.secondary',
                                    }}
                                >
                                    {fmtUpdated(gate.updated_at)}
                                </TableCell>
                                <TableCell>
                                    <GateDecisionCell
                                        gate={gate}
                                        readOnly={readOnly}
                                        mode={mode}
                                    />
                                </TableCell>
                            </TableRow>
                            {hasTargetContext && (
                                <TableRow
                                    sx={{
                                        bgcolor: highlighted
                                            ? alpha(
                                                  theme.palette.primary.main,
                                                  0.08
                                              )
                                            : undefined,
                                        opacity: stale ? 0.5 : 1,
                                    }}
                                >
                                    <TableCell
                                        colSpan={6}
                                        sx={{
                                            // Reads as part of the card above:
                                            // no separator between the two rows.
                                            borderTop: 'none',
                                            pt: 0,
                                            pb: 1.25,
                                            borderColor: alpha(
                                                theme.palette.divider,
                                                0.4
                                            ),
                                        }}
                                    >
                                        <TargetStrip target={gate.target} />
                                    </TableCell>
                                </TableRow>
                            )}
                            </Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </Box>
    );
}
